import { msg } from '@lingui/core/macro';
import { StageV2 } from 'modules/championship/domain/interfaces/stage';
import { UpsertChampionshipStageParams } from 'modules/championship/domain/repositories/stage.repository';
import { upsertStage } from 'modules/championship/store/stages/stages.actions';
import { useCallback } from 'react';
import { useAppDispatch } from 'store/hooks';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';

/** Сохраняет этап и предупреждает, если backend не стал пересоздавать сетку плей-офф.
 *
 * Этап в этом случае сохранён — сообщение живёт здесь, а не в репозитории или reducer'е,
 * и все три экрана, сохраняющие этап, показывают его одинаково.
 *
 * Возвращает `undefined`, если сохранить не удалось. */
export function useUpsertStage() {
  const dispatch = useAppDispatch();
  const toastApiError = useToastApiError();

  return useCallback(
    async (params: UpsertChampionshipStageParams): Promise<StageV2 | undefined> => {
      // Раньше провал молча превращался в `undefined`; теперь — тост с причиной и тот же `undefined`.
      const result = await dispatch(upsertStage(params))
        .unwrap()
        .catch((error) => {
          toastApiError(error, msg`Не удалось сохранить этап`);
          return undefined;
        });

      if (!result) return undefined;

      if (result.bracketsError) {
        // Причину даёт реестр: у 4015 он называет все четыре условия, при которых матч нельзя удалить.
        toastApiError(result.bracketsError, msg`Этап сохранён, но сетку матчей не пересоздали`);
      }

      return result.stage;
    },
    [dispatch, toastApiError],
  );
}

export default useUpsertStage;
